import type { VoidFunctionComponent } from 'react';

import { Category } from '../../../../../shared/issuesV2';
import Subnav from '../../../generic/Subnav';
import SubnavCountBadge from '../../../generic/Subnav/SubnavCountBadge';

import useReleaseGroupSubnav from './useReleaseGroupSubnav';

type Props = {
  projectGroupId: string;
  releaseId?: number;
};

/**
 * ReleaseGroupSubnavItemIssues is the subnav dropdown item for release group issues.
 */
const ReleaseGroupSubnavItemIssues: VoidFunctionComponent<Props> = ({
  projectGroupId,
  releaseId,
}) => {
  const baseUrl = `/projects/group/${projectGroupId}/issues`;
  const {
    categorySummary,
    totalIssuesCount,
    isCategorySummaryLoading,
    hasSecurity,
    hasQuality,
  } = useReleaseGroupSubnav(projectGroupId, releaseId);

  if (!hasSecurity && !hasQuality) {
    return (
      <Subnav.ItemWithCount
        to={baseUrl}
        navKey="issues"
        count={totalIssuesCount || 0}
        isLoading={isCategorySummaryLoading}
      >
        Issues
      </Subnav.ItemWithCount>
    );
  }

  return (
    <Subnav.Dropdown
      to={baseUrl}
      navKey="issues"
    >
      <Subnav.Dropdown.RootItem>
        Issues
        <SubnavCountBadge count={totalIssuesCount || 0} isLoading={isCategorySummaryLoading} />
      </Subnav.Dropdown.RootItem>
      <Subnav.Dropdown.Item
        to={`${baseUrl}/licensing`}
        navKey="issues/licensing"
      >
        Licensing
        <SubnavCountBadge
          count={categorySummary?.[Category.Licensing] ?? 0}
          isLoading={isCategorySummaryLoading}
        />
      </Subnav.Dropdown.Item>
      {hasSecurity && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/vulnerability`}
          navKey="issues/vulnerability"
        >
          Security
          <SubnavCountBadge
            count={categorySummary?.[Category.Vulnerability] ?? 0}
            isLoading={isCategorySummaryLoading}
          />
        </Subnav.Dropdown.Item>
      )}
      {hasQuality && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/quality`}
          navKey="issues/quality"
        >
          Quality
          <SubnavCountBadge
            count={categorySummary?.[Category.Quality] ?? 0}
            isLoading={isCategorySummaryLoading}
          />
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ReleaseGroupSubnavItemIssues;
